import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, CalculatorIcon, ClipboardCheckIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, TargetIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// МАТЕМАТИКА СПРЕМНА™ – Serbian (ekavian, Cyrillic) copy. Source of truth: PRODUCTO.md.
export const BRAND = 'МАТЕМАТИКА СПРЕМНА™';
export const heroGuarantees = ['Тренутни дигитални приступ', 'Једнократно плаћање', 'Без претплате', '7 дана гаранције задовољства'];
export const stats = [{
  value: '240',
  label: 'Страна у шест томова'
}, {
  value: '6',
  label: 'Дигиталних томова'
}, {
  value: '8',
  label: 'Разреда – од 1. до 8.'
}, {
  value: '5',
  label: 'Бонуса у пакету'
}, {
  value: '3',
  label: 'Нивоа у наставним листићима'
}];
export const differenceStats = [{
  value: '1–8. разред',
  label: 'Разредна и предметна настава'
}, {
  value: '6 дигиталних томова',
  label: 'Од бројева и рачунања и геометрије до алгебре, функција и оцењивања'
}, {
  value: 'PDF спреман за штампу',
  label: 'Користите материјал дигитално или га одштампајте за одељење'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Вечери и викенди нестају',
  text: 'Проводите вечери пред екраном планирајући часове математике за следећу недељу – а посао и даље није готов.'
}, {
  title: 'Бескрајна потрага за задацима',
  text: 'Задаци са различитих сајтова су прелаки, претешки или морају да се прилагоде пре него што могу да се користе на часу.'
}, {
  title: 'Велике разлике у одељењу',
  text: 'Некима треба очигледност и подршка, други заврше за пет минута – а час мора да функционише за све.'
}, {
  title: 'Ученици који заостају',
  text: 'Видите да неким ученицима недостаје разумевање месне вредности, разломака или једначина – али немате времена да правите нови материјал.'
}, {
  title: 'Оцењивање и документација одузимају време',
  text: 'Образовни стандарди, формативно оцењивање, ес-Дневник, оперативни планови и дневне припреме – како све то да образложите и документујете?'
}, {
  title: 'Стрес пред почетак часа',
  text: 'Када понестане времена, час се лако претвори у „урадите задатке 1–20 из уџбеника“ и импровизацију у последњем тренутку.'
}];
export const afterItems = ['Готов годишњи и оперативни план за 1–8. разред', '12 готових часова минут по минут', 'Материјал и упутство за учитеља уз сваки час', 'Наставни листићи у 3 нивоа', 'Решења за све задатке', 'Усклађено са Планом и програмом наставе и учења', 'Текстуални задаци и математика у свакодневном животу', 'Контролни задаци, критеријуми оцењивања и готови коментари', 'PDF спреман за штампу'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Вечери одлазе на планирање',
  text: 'Да ли вечери и викенде проводите планирајући часове математике за следећу недељу?'
}, {
  icon: FolderSearchIcon,
  title: 'Тражите праве задатке',
  text: 'Да ли стално тражите на интернету задатке који одговарају разреду и Плану и програму?'
}, {
  icon: UsersIcon,
  title: 'Велике разлике у одељењу',
  text: 'Да ли вам требају задаци који функционишу и за ученике којима треба подршка и за оне који траже изазов?'
}, {
  icon: TargetIcon,
  title: 'Неки ученици заостају',
  text: 'Видите ученике без чврсте основе у рачунању, а тешко налазите време за допунски материјал?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Оцењивање одузима време',
  text: 'Желите готове контролне задатке, критеријуме и коментаре – и помоћ у припреми ученика за завршни испит?'
}, {
  icon: LayoutGridIcon,
  title: 'Недостаје вам јасна структура',
  text: 'Желите да све буде на једном месту, да брзо нађете прави час када почиње настава?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Том 01',
  level: '1–4. разред',
  title: 'Бројеви и рачунање',
  tagline: 'Тачно знате шта радите на сваком часу – целе школске године.',
  intro: 'Први том вам даје готов годишњи и оперативни план за 1–4. разред и дванаест готових часова које можете одмах да одржите.',
  items: ['Годишњи и оперативни план за 1–4. разред', '36 наставних недеља по Плану и програму', '12 готових часова минут по минут', 'Часови од 45 минута', 'Материјал и упутство за учитеља уз сваки час', 'Бројеви до 100, до 1000 и вишецифрени бројеви', 'Месна вредност: јединице, десетице, стотине', 'Сабирање и одузимање са прелазом', 'Таблица множења, дељење и рачунање напамет', 'Текстуални задаци', 'Листићи у 3 нивоа, решења и контролни задаци'],
  outro: 'Готови часови, да у учионицу уђете са јасним планом.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 02',
  level: '1–4. разред',
  title: 'Геометрија, мерење и подаци',
  tagline: 'Математика постаје конкретна и видљива.',
  intro: 'Други том води ученике од линија и фигура до мерења, сата, новца и првих дијаграма.',
  items: ['Линије и фигуре', 'Тачка, права, дуж и круг', 'Мерење дужине, масе и запремине', 'Мерење времена, сат и новац (динари)', 'Табеле и стубични дијаграми', 'Обим и површина правоугаоника и квадрата', 'Листићи у 3 нивоа', 'Игре и решења'],
  outro: 'Практична математика коју ученици могу да виде и опипају.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 03',
  level: '5–6. разред',
  title: 'Природни бројеви, дељивост и разломци',
  tagline: 'Чврст појам броја – и у тежем градиву.',
  intro: 'Трећи том показује ученицима како су повезани дељивост, разломци, децимални запис и проценти.',
  items: ['Скупови', 'Дељивост', 'НЗД и НЗС', 'Разломци и децимални запис', 'Проценти', 'Угао', 'Осна симетрија', 'Готови часови, листићи у 3 нивоа и решења'],
  outro: 'Јасни часови који разломке чине разумљивим за све.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Том 04',
  level: '5–6. разред',
  title: 'Цели и рационални бројеви, троугао и четвороугао',
  tagline: 'Од целих бројева до једначина, троугла и четвороугла.',
  intro: 'Четврти том уводи целе и рационалне бројеве, изразе и једначине и продубљује геометрију у 5. и 6. разреду.',
  items: ['Цели бројеви', 'Рационални бројеви', 'Изрази и једначине', 'Троугао', 'Четвороугао', 'Површина', 'Проценат у свакодневном животу', 'Часови, листићи и решења'],
  outro: 'Практична помоћ да апстрактне појмове предајете конкретно.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 05',
  level: '7–8. разред',
  title: 'Реални бројеви, алгебра и функције',
  tagline: 'Срж математике у 7. и 8. разреду – спремна.',
  intro: 'Пети том покрива реалне бројеве, алгебру и функције у старијим разредима.',
  items: ['Реални бројеви и квадратни корен', 'Степен', 'Полиноми', 'Питагорина теорема', 'Линеарне једначине и неједначине', 'Линеарна функција', 'Системи једначина', 'Часови, листићи у 3 нивоа и решења'],
  outro: 'Готови часови који алгебру и функције отварају целом одељењу.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Том 06',
  level: '7–8. разред',
  title: 'Геометрија, оцењивање и завршни испит',
  tagline: 'Видите шта ученици знају – и припремите их за завршни испит.',
  intro: 'Шести том спаја геометрију са алатима за оцењивање и припрему за завршни испит из математике.',
  items: ['Многоугао и круг', 'Сличност', 'Призма, пирамида, ваљак, купа и лопта', 'Критеријуми оцењивања (1–5) у 3 нивоа', 'Контролни задаци А/Б са решењима', 'Припрема за завршни испит из математике', '100 готових коментара за формативно оцењивање'],
  outro: 'Пуна подршка за праведно оцењивање и спремне ученике.',
  note: 'Материјал је развијен независно. Није издање нити одобрење Министарства просвете.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 дигиталних томова из математике',
  text: 'Комплетан наставни систем који вам помаже да планирате и изведете наставу математике током целе школске године. Сваки том има 40 страна са готовим часовима, листићима у 3 нивоа и решењима.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Готови часови минут по минут',
  text: 'Крај празном папиру. Јасне припреме за час са током часа, материјалом и упутством за учитеља, да брзо почнете.'
}, {
  icon: FileTextIcon,
  title: 'Листићи у 3 нивоа',
  text: 'Исти циљ у три нивоа – основни, средњи и напредни – да сви ученици раде исто градиво.'
}, {
  icon: CalculatorIcon,
  title: 'Решења за све задатке',
  text: 'Решења за листиће, задатке и контролне задатке, да уштедите време на прегледању.'
}, {
  icon: LayersIcon,
  title: 'Усклађено са Планом и програмом',
  text: 'Исходи из Плана и програма наставе и учења и образовни стандарди за 1–4, 5–6. и 7–8. разред – јасно повезани са часовима.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Оцењивање',
  text: 'Критеријуми оцењивања (1–5), контролни задаци А/Б, припрема за завршни испит и 100 готових коментара за формативно оцењивање.'
}, {
  icon: CalendarCheckIcon,
  title: 'Годишњи и оперативни план',
  text: 'Помоћ да структурирате целу школску годину – 36 наставних недеља, по разредима.'
}];
// Real pages from Том 1 FINAL (Gamma Studio), reviewed page by page (gamma/REVISION_TOM1_FIX.md, all OK).
const PAGES = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/8a79eca2-c65c-4ea1-a47c-766d506cf788/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: PAGES + '1792716343287_ms-tom1-strana03-bd359641.webp',
  label: 'Том 1 · Страна 3',
  title: 'Садржај Тома 1',
  text: 'Годишњи планови за 1–4. разред, оперативни план, 12 готових часова са листићима, контролни задаци и решења.'
}, {
  src: PAGES + '1792716346742_ms-tom1-strana12-17b2a303.webp',
  label: 'Том 1 · Страна 12',
  title: 'Час 1 · Наставни листић „Бројеви до 10“',
  text: 'Исти циљ у 3 нивоа – основни, средњи и напредни – са решењима за учитеља на дну стране.'
}, {
  src: PAGES + '1792716350019_ms-tom1-strana23-20d6e694.webp',
  label: 'Том 1 · Страна 23',
  title: 'Час 7 · 2. разред · 45 минута',
  text: 'Сабирање и одузимање до 100: циљ, материјал, ток часа минут по минут и упутство за учитеља.'
}, {
  src: PAGES + '1792716353731_ms-tom1-strana28-73b248d5.webp',
  label: 'Том 1 · Страна 28',
  title: 'Час 9 · Наставни листић „Таблица множења“',
  text: 'Задаци у 3 нивоа, од основног до напредног, са решењима за брзо прегледање.'
}, {
  src: PAGES + '1792716361766_ms-tom1-strana35-bda409ca.webp',
  label: 'Том 1 · Страна 35',
  title: 'Контролни задатак – 1. разред',
  text: 'Десет задатака спремних за штампу; решења и бодовање налазе се на крају тома.'
}];
export const pageTypes = ['Готови часови', 'Листићи у 3 нивоа', 'Решења', 'Оцењивање'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Годишњи и оперативни планови из математике – за 1–8. разред',
  text: 'Готови годишњи и оперативни планови за целу школску годину, да увек знате шта следи наредне недеље.',
  items: ['Планови за 1–8. разред', '36 наставних недеља', 'По Плану и програму', 'Спремно за штампу'],
  benefit: 'Цела година испланирана – пре него што почне.'
}, {
  title: '100 додатних наставних листића из математике – са решењима',
  text: 'Додатно увежбавање за ученике којима треба више, за оне који брзо заврше – или за домаћи задатак.',
  items: ['100 листића', 'За све разреде', 'Различите тежине', 'Решења укључена'],
  benefit: 'Увек имате нешто смислено за ученике.'
}, {
  title: 'Математичке игре – 40 игара без припреме',
  text: 'Игре које увежбавање и понављање чине активним и забавним. Правила су написана јасно.',
  items: ['40 игара', 'Без припреме', 'За цело одељење, у паровима и групама', 'Рачунање напамет и појам броја'],
  benefit: 'Више мотивације – мање досаде.'
}, {
  title: '5 минута рачунања напамет – 180 загревања за целу школску годину',
  text: 'Кратка загревања на почетку часа: ученици раде математику од првог минута.',
  items: ['180 загревања', 'За целу школску годину', 'По 5 минута', 'Решења укључена'],
  benefit: 'Сваки час почиње мирно и фокусирано.'
}, {
  title: 'Пакет за замену – 12 готових часова математике',
  text: 'Дванаест готових часова које колега на замени може да одржи без припреме.',
  items: ['12 готових часова', 'Упутство за наставника на замени', 'Листићи са решењима', 'Спремно за штампу'],
  benefit: 'Ниједан изгубљен час када сте одсутни.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Отворите пакет',
  text: 'Преузмите МАТЕМАТИКА СПРЕМНА™ и отворите том за свој разред.'
}, {
  title: 'Изаберите разред и час',
  text: 'Пронађите час, тему или листић који одговара вашем одељењу.'
}, {
  title: 'Припремите се за 5 минута',
  text: 'Прочитајте упутство за учитеља и припремите материјал који вам треба.'
}, {
  title: 'Одштампајте и почните час',
  text: 'Одштампајте листиће или користите материјал дигитално – и уђите у учионицу са јасним планом.'
}];
export const audienceItems = ['Предајете математику од 1. до 8. разреда', 'Радите у разредној настави (1–4. разред)', 'Предајете математику у предметној настави (5–8. разред)', 'Желите готов годишњи и оперативни план по Плану и програму', 'Радите у одељењима са великим разликама у знању', 'Желите листиће у 3 нивоа са решењима', 'Треба вам помоћ око оцењивања и завршног испита', 'Желите да вам вечери и викенди буду слободни'];
export const timeValueTasks = ['Тражење задатака на интернету', 'Писање дневних припрема', 'Прављење листића у више нивоа', 'Прављење решења и контролних задатака', 'Оцењивање и писање коментара'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 дигиталних томова – 240 страна',
  detail: 'Бројеви и рачунање, геометрија и мерење, разломци, рационални и реални бројеви, алгебра, функције и оцењивање'
}, {
  label: '5 бонуса без доплате',
  detail: 'Годишњи и оперативни планови, 100 додатних листића, Математичке игре, 5 минута рачунања напамет и Пакет за замену'
}, {
  label: 'За 1–8. разред',
  detail: 'Готови часови, листићи у 3 нивоа, решења и критеријуми оцењивања'
}];
export const offerIncludes = ['Тренутни дигитални приступ', 'PDF фајлови које чувате', 'Спремно за штампу', 'Ништа се не шаље поштом'];
// Serbian dinar (RSD), Serbian notation: "2.490 дин." = Market «Србија».
export const PRICE = '2.490 дин.';
export const PRICE_BEFORE = '5.990 дин.';

// Only add authentic reviews of МАТЕМАТИКА СПРЕМНА. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Како добијам материјал?',
  a: 'МАТЕМАТИКА СПРЕМНА™ се испоручује дигитално. После куповине добијате приступ PDF фајловима које можете да преузмете и сачувате. Материјал користите на рачунару, таблету или телефону и штампате стране које су вам потребне.'
}, {
  q: 'Да ли је материјал усклађен са Планом и програмом?',
  a: 'Да. Годишњи и оперативни планови и часови прате План и програм наставе и учења за математику и образовне стандарде (основни, средњи и напредни ниво) за 1–4, 5–6. и 7–8. разред. МАТЕМАТИКА СПРЕМНА™ је развијена независно. Није издање нити одобрење Министарства просвете.'
}, {
  q: 'За које разреде је материјал?',
  a: 'Материјал покрива 1–8. разред: Том 1 и 2 су за 1–4. разред, Том 3 и 4 за 5–6. разред, а Том 5 и 6 за 7–8. разред. Ви бирате часове и задатке према свом одељењу.'
}, {
  q: 'Могу ли заиста да припремим час за 5 минута?',
  a: 'МАТЕМАТИКА СПРЕМНА™ је направљена да припрема буде брза. Отворите том, изаберете готов час и прочитате га за око пет минута. Време може да варира у зависности од часа и потребе за диференцијацијом.'
}, {
  q: 'Да ли је материјал довољан за целу школску годину?',
  a: 'Пакет садржи годишњи и оперативни план за 36 наставних недеља и готове часове у свим томовима. Материјал бирате, комбинујете и прилагођавате свом плану и потребама ученика.'
}, {
  q: 'Има ли листића у различитим нивоима?',
  a: 'Да. Наставни листићи постоје у 3 нивоа – основни, средњи и напредни, као у образовним стандардима – да ученици са различитим потребама раде ка истом циљу.'
}, {
  q: 'Да ли су решења укључена?',
  a: 'Да. Решења постоје за листиће, задатке и контролне задатке, да уштедите време на прегледању.'
}, {
  q: 'Да ли је оцењивање укључено?',
  a: 'Да. Том 6 садржи критеријуме оцењивања (1–5) у 3 нивоа, контролне задатке А/Б са решењима, припрему за завршни испит из математике и 100 готових коментара за формативно оцењивање.'
}, {
  q: 'Може ли материјал да се користи у допунској настави и уз ИОП?',
  a: 'Листићи у 3 нивоа и јасни циљеви олакшавају диференцијацију – у одељењу, у допунској и додатној настави и уз индивидуални образовни план. Материјал не замењује процедуре школе нити сарадњу са педагогом и психологом.'
}, {
  q: 'Да ли морам све да одштампам?',
  a: 'Не. Сами бирате које стране штампате, а део материјала можете да користите и дигитално.'
}, {
  q: 'Да ли је пет бонуса укључено у цену?',
  a: `Да. Свих пет бонуса је у пакету без доплате – укупно ${PRICE}.`
}, {
  q: 'Да ли је ово претплата?',
  a: 'Не. Плаћате једном и добијате приступ дигиталном материјалу. Без месечних плаћања.'
}, {
  q: 'Могу ли да користим материјал на телефону или таблету?',
  a: 'Да. PDF фајлови се отварају на рачунару, таблету и телефону. Препоручујемо рачунар или таблет када прегледате листиће пре штампања.'
}, {
  q: 'Постоји ли гаранција?',
  a: 'Да. МАТЕМАТИКА СПРЕМНА™ има гаранцију задовољства од 7 дана. Ако противно очекивањима нисте задовољни материјалом, можете да нам пишете и да добијете повраћај новца у гарантном року, према условима гаранције.'
}];
